import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Check, X, Baby, Lock, Clock, Shield, RotateCcw, Sparkles, PlayCircle, Plus, Trash2, Pencil, Users, Mail, Send, CheckCircle2, Mic, StopCircle, Volume2, Trash } from "lucide-react";

/**
 * ParentDashboard — combined parent-account hub:
 *   1. Enable-parent CTA (any user can self-designate as a parent).
 *   2. Kids Mode controls — PIN, daily time limit, allowed-hour window,
 *      today's minutes, 14-day history, reset-today.
 *   3. Pending kid uploads — review/approve/reject (only rendered when
 *      the user has `kid_user_ids` linked, i.e. they actually accepted
 *      a child-invite flow).
 */
export default function ParentDashboard() {
  const { user, refresh } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [dash, setDash] = useState(null);
  const [kids, setKids] = useState([]);
  const [pending, setPending] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [profilesMeta, setProfilesMeta] = useState({ max: 3, avatars: [] });
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [limit, setLimit] = useState(60);
  const [windowStart, setWindowStart] = useState("");
  const [windowEnd, setWindowEnd] = useState("");
  const [savingPin, setSavingPin] = useState(false);
  const [savingLimit, setSavingLimit] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [newProfileOpen, setNewProfileOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [digestCfg, setDigestCfg] = useState({ webhook_url: "", send_hour_utc: 20, enabled: true });
  const [digestToday, setDigestToday] = useState(null);
  const [digestHistory, setDigestHistory] = useState([]);
  const [digestSaving, setDigestSaving] = useState(false);
  const [digestFiring, setDigestFiring] = useState(false);
  const [weeklyDigest, setWeeklyDigest] = useState(null);
  const [weeklyHighlights, setWeeklyHighlights] = useState(null);
  const [artwork, setArtwork] = useState([]);
  const [artworkOpen, setArtworkOpen] = useState(null); // currently previewed drawing
  const [artworkLoading, setArtworkLoading] = useState(false);

  const isParent = !!user?.is_parent;

  const loadAll = () => {
    api.get("/kids/config").then((r) => {
      setCfg(r.data);
      if (r.data?.daily_limit_min) setLimit(r.data.daily_limit_min);
      if (r.data?.window_start_hh !== undefined && r.data?.window_start_hh !== null) setWindowStart(String(r.data.window_start_hh));
      if (r.data?.window_end_hh !== undefined && r.data?.window_end_hh !== null) setWindowEnd(String(r.data.window_end_hh));
    }).catch(() => {});
    api.get("/parent/dashboard").then((r) => setDash(r.data)).catch(() => {});
    if (isParent) {
      api.get("/parent/kids").then((r) => setKids(r.data || [])).catch(() => setKids([]));
      api.get("/parent/pending-videos").then((r) => setPending(r.data || [])).catch(() => setPending([]));
      api.get("/kids/profiles").then((r) => {
        setProfiles(r.data?.profiles || []);
        setProfilesMeta({ max: r.data?.max || 3, avatars: r.data?.avatars || [] });
      }).catch(() => {});
      api.get("/parent/digest/config").then((r) => setDigestCfg(r.data || {})).catch(() => {});
      api.get("/parent/digest/today").then((r) => setDigestToday(r.data || null)).catch(() => {});
      api.get("/parent/digest/history?limit=14").then((r) => setDigestHistory(r.data?.digests || [])).catch(() => {});
      api.get("/kids/weekly-digest").then((r) => setWeeklyDigest(r.data)).catch(() => {});
      api.get("/parent/kids/highlights").then((r) => setWeeklyHighlights(r.data)).catch(() => {});
      setArtworkLoading(true);
      api.get("/parent/kids/drawings?limit=60")
        .then((r) => setArtwork(r.data?.drawings || []))
        .catch(() => setArtwork([]))
        .finally(() => setArtworkLoading(false));
    }
  };
  useEffect(() => { if (user) loadAll(); /* eslint-disable-next-line */ }, [user, isParent]);

  if (!user) {
    return (
      <div data-testid="parent-login-required" className="pt-32 px-6 text-center">
        <div className="text-white font-display text-2xl">Sign in to access Parent Dashboard</div>
        <Link to="/login" className="inline-block mt-4 bg-tv-accent text-black font-bold px-5 py-2 rounded-full">Sign in</Link>
      </div>
    );
  }

  const enableParent = async () => {
    setEnabling(true);
    try {
      await api.post("/parent/enable");
      toast.success("Parent account enabled — kids content unlocked");
      if (refresh) await refresh();
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEnabling(false); }
  };

  const savePin = async () => {
    if (pin.length < 4) { toast.error("PIN must be at least 4 digits"); return; }
    if (pin !== pinConfirm) { toast.error("PINs don't match"); return; }
    setSavingPin(true);
    try {
      await api.post("/kids/pin", { pin });
      toast.success("PIN saved");
      setPin(""); setPinConfirm("");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingPin(false); }
  };

  const saveLimit = async () => {
    setSavingLimit(true);
    try {
      const body = { daily_limit_min: Number(limit) };
      if (windowStart !== "") body.window_start_hh = Number(windowStart);
      if (windowEnd !== "") body.window_end_hh = Number(windowEnd);
      await api.patch("/kids/config", body);
      toast.success("Limits updated");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingLimit(false); }
  };

  const resetToday = async () => {
    try {
      await api.post("/parent/reset-today");
      toast.success("Today's timer reset to 0");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveDigest = async () => {
    setDigestSaving(true);
    try {
      await api.post("/parent/digest/config", {
        webhook_url: digestCfg.webhook_url || "",
        send_hour_utc: Number(digestCfg.send_hour_utc ?? 20),
        enabled: !!digestCfg.enabled,
      });
      toast.success("Digest settings saved");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDigestSaving(false); }
  };

  const fireDigestNow = async () => {
    setDigestFiring(true);
    try {
      const { data } = await api.post("/parent/digest/test");
      const wr = data.webhook_result;
      if (wr && wr.ok) toast.success(`Digest sent — webhook replied ${wr.status}`);
      else if (wr) toast.error(`Webhook failed (${wr.status || "err"})`);
      else toast.success("Digest built (no webhook configured — in-app only)");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDigestFiring(false); }
  };

  const approve = async (vid) => {
    await api.post(`/parent/videos/${vid}/approve`);
    toast.success("Approved — sending to admin review");
    loadAll();
  };
  const reject = async (vid) => {
    await api.post(`/parent/videos/${vid}/reject`);
    toast.info("Rejected");
    loadAll();
  };

  return (
    <div data-testid="parent-dashboard" className="pt-32 pb-20 px-4 sm:px-8 md:px-12 max-w-6xl mx-auto">
      <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold">Parent Monitoring</div>
      <h1 className="font-display font-black text-4xl md:text-5xl text-white tracking-tighter mt-2">
        Hi {user.name || user.display_name || "there"}.
      </h1>
      <p className="text-white/70 mt-2 text-sm">
        Configure Kids Mode, set screen-time limits, and review your kids' activity.
      </p>

      {/* --- 1. Enable parent account CTA --- */}
      {!isParent && (
        <section data-testid="enable-parent-section" className="mt-8 tv-glass rounded-2xl p-6 border border-tv-accent/40 bg-gradient-to-br from-tv-accent/10 to-fuchsia-500/10">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-tv-accent/20 flex items-center justify-center shrink-0">
              <Shield className="w-6 h-6 text-tv-accent" />
            </div>
            <div className="flex-1">
              <h2 className="font-display font-bold text-2xl text-white">Enable parent account</h2>
              <p className="text-white/70 mt-1 text-sm">
                Turn on parent status to unlock Kids Mode — a safe, curated experience for children with PIN-gated exit, screen-time limits, and a kids-only content allowlist.
              </p>
              <button
                data-testid="enable-parent-btn"
                onClick={enableParent}
                disabled={enabling}
                className="mt-4 inline-flex items-center gap-2 bg-tv-accent hover:bg-tv-accent/90 disabled:opacity-60 text-black font-bold px-5 py-2.5 rounded-full"
              >
                <Sparkles className="w-4 h-4" /> {enabling ? "Enabling…" : "Enable parent account"}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* --- 2. Quick link to /tv/kids --- */}
      {isParent && (
        <section data-testid="kids-tv-link-section" className="mt-8 tv-glass rounded-2xl p-5 border border-tv-accent/30 bg-gradient-to-br from-fuchsia-500/10 to-sky-500/10 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-tv-accent/20 flex items-center justify-center shrink-0">
            <PlayCircle className="w-6 h-6 text-tv-accent" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-white text-lg">Open Kids TV</div>
            <div className="text-white/60 text-sm">Hand the device to your kid — safe content only.</div>
          </div>
          <Link
            to="/tv/kids"
            data-testid="open-tv-kids-btn"
            className="bg-tv-accent hover:bg-tv-accent/90 text-black font-bold px-5 py-2.5 rounded-full"
          >Open</Link>
        </section>
      )}

      {/* --- 2b. Kid profiles --- */}
      {isParent && (
        <section data-testid="profiles-section" className="mt-6 tv-glass rounded-2xl p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-5 h-5 text-tv-accent" />
                <h2 className="font-display font-bold text-2xl text-white">Kid profiles</h2>
                <span className="ml-2 text-xs bg-white/10 text-white/70 px-2 py-0.5 rounded-full">{profiles.length}/{profilesMeta.max}</span>
              </div>
              <p className="text-white/60 text-sm">Each kid gets their own playful avatar, screen-time budget, and watch history.</p>
            </div>
            <button
              data-testid="profile-new-btn"
              onClick={() => setNewProfileOpen(true)}
              disabled={profiles.length >= profilesMeta.max}
              className="inline-flex items-center gap-1.5 bg-tv-accent disabled:opacity-40 hover:bg-tv-accent/90 text-black font-bold px-4 py-2 rounded-full"
            ><Plus className="w-4 h-4" /> New profile</button>
          </div>

          {profiles.length === 0 ? (
            <div data-testid="profiles-empty" className="mt-4 text-white/60 text-sm">No kid profiles yet. Add one to give each child their own avatar + limits.</div>
          ) : (
            <div className="mt-4 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {profiles.map((p) => (
                <div key={p.profile_id} data-testid={`profile-card-${p.profile_id}`} className="bg-black/40 border border-white/10 rounded-2xl p-4 flex items-center gap-3">
                  <div className="text-5xl shrink-0">{p.avatar_emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-white text-lg truncate">{p.name}</div>
                    <div className="text-xs text-white/60">
                      {p.today_watched_min}/{p.daily_limit_min} min today
                      {p.locked && <span className="ml-1 text-red-300 font-bold">· Locked</span>}
                    </div>
                  </div>
                  <button
                    data-testid={`profile-edit-${p.profile_id}`}
                    onClick={() => setEditingProfile(p)}
                    className="p-2 rounded-full bg-white/10 hover:bg-white/15 text-white"
                    title="Edit"
                  ><Pencil className="w-4 h-4" /></button>
                  <button
                    data-testid={`profile-delete-${p.profile_id}`}
                    onClick={async () => {
                      if (!window.confirm(`Delete ${p.name}'s profile?`)) return;
                      try { await api.delete(`/kids/profiles/${p.profile_id}`); toast.success("Profile deleted"); loadAll(); }
                      catch (e) { toast.error(formatApiError(e)); }
                    }}
                    className="p-2 rounded-full bg-red-500/15 hover:bg-red-500/25 text-red-300"
                    title="Delete"
                  ><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* --- 3. PIN Setup --- */}
      <MyKidsWeek digest={weeklyDigest} />
      <VoiceSettingsSection />

      <section data-testid="pin-section" className="mt-8 tv-glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-1">
          <Lock className="w-5 h-5 text-tv-accent" />
          <h2 className="font-display font-bold text-2xl text-white">PIN lock</h2>
          {cfg?.pin_set && (
            <span data-testid="pin-status-set" className="ml-2 text-xs bg-green-500/20 text-green-300 px-2 py-0.5 rounded-full font-bold">PIN set</span>
          )}
          {!cfg?.pin_set && (
            <span data-testid="pin-status-unset" className="ml-2 text-xs bg-yellow-500/20 text-yellow-300 px-2 py-0.5 rounded-full font-bold">No PIN</span>
          )}
        </div>
        <p className="text-white/60 text-sm mb-4">
          A 4–6 digit PIN your kid has to ask you for before they can exit Kids TV.
        </p>
        <div className="grid sm:grid-cols-2 gap-3 max-w-xl">
          <input
            data-testid="pin-input"
            type="password"
            inputMode="numeric"
            maxLength={6}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            placeholder="New PIN"
            className="bg-black/40 border border-white/10 rounded-lg px-4 py-2.5 text-white tracking-[0.4em] text-center font-bold outline-none focus:border-tv-accent"
          />
          <input
            data-testid="pin-confirm-input"
            type="password"
            inputMode="numeric"
            maxLength={6}
            value={pinConfirm}
            onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, ""))}
            placeholder="Confirm PIN"
            className="bg-black/40 border border-white/10 rounded-lg px-4 py-2.5 text-white tracking-[0.4em] text-center font-bold outline-none focus:border-tv-accent"
          />
        </div>
        <button
          data-testid="pin-save-btn"
          onClick={savePin}
          disabled={savingPin || pin.length < 4 || pin !== pinConfirm}
          className="mt-4 bg-tv-accent disabled:opacity-40 hover:bg-tv-accent/90 text-black font-bold px-5 py-2 rounded-full"
        >{savingPin ? "Saving…" : (cfg?.pin_set ? "Change PIN" : "Save PIN")}</button>
      </section>

      {/* --- 4. Time limits --- */}
      <section data-testid="time-limits-section" className="mt-6 tv-glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-1">
          <Clock className="w-5 h-5 text-tv-accent" />
          <h2 className="font-display font-bold text-2xl text-white">Daily time limits</h2>
        </div>
        <p className="text-white/60 text-sm mb-4">
          How much Kids TV can happen per day, and optionally during which hours.
        </p>

        {cfg && (
          <div data-testid="today-summary" className="mb-4 flex flex-wrap gap-3">
            <StatChip label="Watched today" value={`${cfg.today_watched_min} min`} />
            <StatChip label="Remaining" value={`${cfg.remaining_min} min`} />
            <StatChip label="Daily cap" value={`${cfg.daily_limit_min} min`} />
            {cfg.locked && <StatChip label="Status" value="Locked out" red />}
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-3 max-w-2xl">
          <div>
            <div className="text-xs text-white/60 mb-1">Daily cap (minutes)</div>
            <input
              data-testid="limit-input"
              type="number"
              min={10}
              max={720}
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-accent"
            />
          </div>
          <div>
            <div className="text-xs text-white/60 mb-1">Allowed from (hour, optional)</div>
            <input
              data-testid="window-start-input"
              type="number"
              min={0}
              max={23}
              value={windowStart}
              onChange={(e) => setWindowStart(e.target.value)}
              placeholder="e.g. 7"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-accent"
            />
          </div>
          <div>
            <div className="text-xs text-white/60 mb-1">Allowed until (hour, optional)</div>
            <input
              data-testid="window-end-input"
              type="number"
              min={0}
              max={23}
              value={windowEnd}
              onChange={(e) => setWindowEnd(e.target.value)}
              placeholder="e.g. 20"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-accent"
            />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button
            data-testid="limit-save-btn"
            onClick={saveLimit}
            disabled={savingLimit}
            className="bg-tv-accent disabled:opacity-40 hover:bg-tv-accent/90 text-black font-bold px-5 py-2 rounded-full"
          >{savingLimit ? "Saving…" : "Save limits"}</button>
          <button
            data-testid="reset-today-btn"
            onClick={resetToday}
            className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 text-white font-bold px-4 py-2 rounded-full"
          ><RotateCcw className="w-4 h-4" /> Reset today</button>
        </div>
      </section>

      {/* --- 5. 14-day history --- */}
      {dash?.history && (
        <section data-testid="history-section" className="mt-6 tv-glass rounded-2xl p-6">
          <h2 className="font-display font-bold text-2xl text-white mb-3">Last 14 days</h2>
          {dash.history.length === 0 ? (
            <div className="text-white/60 text-sm" data-testid="history-empty">No Kids TV activity yet.</div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
              {dash.history.map((h) => (
                <div key={h.date} data-testid={`history-row-${h.date}`} className="bg-black/40 rounded-lg p-2 text-center">
                  <div className="text-[10px] text-white/50 uppercase tracking-wider">{h.date.slice(5)}</div>
                  <div className="text-white font-black text-xl">{h.minutes || 0}</div>
                  <div className="text-[10px] text-white/50">min</div>
                </div>
              ))}
            </div>
          )}
          <div className="text-xs text-white/50 mt-3">Total last 14 days: <span className="text-white font-bold">{dash.last_14_days_total_min || 0} min</span></div>
        </section>
      )}

      {/* --- 5b. Daily digest --- */}
      {isParent && (
        <section data-testid="digest-section" className="mt-6 tv-glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-1">
            <Mail className="w-5 h-5 text-tv-accent" />
            <h2 className="font-display font-bold text-2xl text-white">Daily digest</h2>
          </div>
          <p className="text-white/60 text-sm mb-4">
            A per-kid watch-time summary sent once a day. Paste a webhook URL (Zapier, Discord, Slack, Make — route it to email, SMS, or your team chat) and pick the hour. Leave the URL blank to keep it in-app only.
          </p>

          {/* Today's summary card */}
          {digestToday && (
            <div data-testid="digest-today" className="mb-5 rounded-xl border border-white/10 bg-black/30 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-white/70 text-sm">Today · {digestToday.date}</div>
                <div className="text-white font-bold text-sm">{digestToday.total_minutes} min total</div>
              </div>
              {digestToday.profile_count === 0 ? (
                <div className="text-white/60 text-sm">No kid profiles yet — add one above to start tracking.</div>
              ) : (
                <div className="space-y-2">
                  {digestToday.profiles.map((p) => {
                    const pct = Math.min(100, Math.round((p.minutes / Math.max(1, p.daily_limit_min)) * 100));
                    return (
                      <div key={p.profile_id} data-testid={`digest-profile-${p.profile_id}`} className="flex items-center gap-3">
                        <div className="text-2xl w-8 text-center">{p.avatar_emoji}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-white font-bold truncate">{p.name}</span>
                            <span className="text-white/70">
                              {p.minutes}/{p.daily_limit_min} min
                              {p.locked && <span className="ml-2 text-red-300 font-bold">· Locked</span>}
                            </span>
                          </div>
                          <div className="mt-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                            <div className={`h-full transition-all ${p.locked ? "bg-red-400" : "bg-tv-accent"}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Config */}
          <div className="grid md:grid-cols-[1fr,180px,160px] gap-3 max-w-3xl">
            <div>
              <div className="text-xs text-white/60 mb-1">Webhook URL (optional)</div>
              <input
                data-testid="digest-webhook-input"
                value={digestCfg.webhook_url || ""}
                onChange={(e) => setDigestCfg((c) => ({ ...c, webhook_url: e.target.value }))}
                placeholder="https://hooks.zapier.com/…"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-accent"
              />
            </div>
            <div>
              <div className="text-xs text-white/60 mb-1">Send hour (UTC)</div>
              <select
                data-testid="digest-hour-select"
                value={digestCfg.send_hour_utc ?? 20}
                onChange={(e) => setDigestCfg((c) => ({ ...c, send_hour_utc: Number(e.target.value) }))}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-accent"
              >
                {Array.from({ length: 24 }).map((_, h) => (
                  <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-xs text-white/60 mb-1">Status</div>
              <label className="inline-flex items-center gap-2 bg-black/40 border border-white/10 rounded-lg px-3 py-2 cursor-pointer w-full">
                <input
                  data-testid="digest-enabled-toggle"
                  type="checkbox"
                  checked={!!digestCfg.enabled}
                  onChange={(e) => setDigestCfg((c) => ({ ...c, enabled: e.target.checked }))}
                />
                <span className="text-white text-sm">{digestCfg.enabled ? "Enabled" : "Paused"}</span>
              </label>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <button
              data-testid="digest-save-btn"
              onClick={saveDigest}
              disabled={digestSaving}
              className="bg-tv-accent disabled:opacity-40 hover:bg-tv-accent/90 text-black font-bold px-5 py-2 rounded-full"
            >{digestSaving ? "Saving…" : "Save settings"}</button>
            <button
              data-testid="digest-fire-btn"
              onClick={fireDigestNow}
              disabled={digestFiring}
              className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 disabled:opacity-40 text-white font-bold px-4 py-2 rounded-full"
            ><Send className="w-4 h-4" /> {digestFiring ? "Sending…" : "Send test now"}</button>
          </div>

          {/* History */}
          {digestHistory.length > 0 && (
            <div className="mt-5">
              <div className="text-xs uppercase tracking-wider text-white/50 font-bold mb-2">Recent digests</div>
              <div data-testid="digest-history" className="space-y-1.5 max-h-64 overflow-y-auto">
                {digestHistory.map((h) => (
                  <div key={h.digest_id} data-testid={`digest-row-${h.digest_id}`} className="flex items-center gap-3 text-sm bg-black/30 border border-white/5 rounded-lg px-3 py-2">
                    <CheckCircle2 className={`w-4 h-4 shrink-0 ${h.webhook_result?.ok ? "text-green-400" : h.webhook_result ? "text-red-400" : "text-white/40"}`} />
                    <span className="text-white/80 w-24 shrink-0">{h.date}</span>
                    <span className="text-white font-bold">{h.summary?.total_minutes || 0} min</span>
                    <span className="text-white/60">· {h.summary?.profile_count || 0} kid{(h.summary?.profile_count || 0) === 1 ? "" : "s"}</span>
                    {h.is_test && <span className="ml-auto text-[10px] bg-white/10 px-2 py-0.5 rounded-full text-white/70">TEST</span>}
                    {h.webhook_result && <span className="ml-auto text-xs text-white/60">webhook {h.webhook_result.status}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}


      {/* --- 5a. Weekly Highlight Reel (all kids recap) --- */}
      {isParent && weeklyHighlights && weeklyHighlights.totals?.kids_count > 0 && (
        <section data-testid="weekly-highlights-section" className="mt-6 rounded-2xl p-6 bg-gradient-to-br from-pink-500/20 via-fuchsia-500/10 to-sky-500/15 border border-white/10">
          <div className="flex items-center gap-3 flex-wrap">
            <img src="/kids-learn-play-logo.png" alt="" className="w-16 h-auto drop-shadow-xl" draggable={false} />
            <div className="flex-1 min-w-0">
              <h2 className="font-display font-black text-2xl text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-300" /> This week's highlights
              </h2>
              <p className="text-white/60 text-sm">
                {weeklyHighlights.totals.total_minutes} total minutes · {weeklyHighlights.totals.total_drawings} drawings shared
              </p>
            </div>
            <button
              data-testid="highlights-share-btn"
              onClick={async () => {
                try {
                  const r = await api.post("/parent/kids/highlights/share", {
                    profiles: weeklyHighlights.profiles,
                    totals: weeklyHighlights.totals,
                  });
                  const url = `${window.location.origin}${r.data.share_url}`;
                  try { await navigator.clipboard.writeText(url); } catch {}
                  toast.success("Share link copied! Send it to grandparents 💛");
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="bg-gradient-to-r from-pink-500 to-fuchsia-500 hover:from-pink-400 hover:to-fuchsia-400 text-white font-black text-sm px-4 py-2 rounded-full inline-flex items-center gap-2"
            >Share 📤</button>
          </div>
          <div data-testid="highlights-grid" className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {weeklyHighlights.profiles.map((h) => (
              <div
                key={h.profile_id}
                data-testid={`highlight-${h.profile_id}`}
                className="bg-gradient-to-br from-white/10 to-white/5 border border-white/15 rounded-2xl p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="text-3xl">{h.avatar_emoji || "⭐"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-black truncate">{h.name}</div>
                    <div className="text-white/60 text-xs">
                      <span className="text-xl align-middle">{h.medal}</span> {h.days_active}/7 days active
                    </div>
                  </div>
                </div>
                <div className="mt-3 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-white/60">Watch time</span><b>{h.week_minutes} min</b></div>
                  <div className="flex justify-between"><span className="text-white/60">Drawings shared</span><b>{h.drawings_shared}</b></div>
                  <div className="flex justify-between"><span className="text-white/60">Top activity</span><b className="capitalize">{h.top_activity || "–"}</b></div>
                </div>
                {/* Usage bar */}
                <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-pink-400 to-fuchsia-500"
                    style={{ width: `${Math.min(100, (h.week_minutes / Math.max(h.weekly_limit_min, 1)) * 100)}%` }}
                  />
                </div>
                <div className="text-[10px] text-white/50 mt-1">of {h.weekly_limit_min} min weekly limit</div>
              </div>
            ))}
          </div>
        </section>
      )}


      {/* --- 5b. Kids artwork gallery --- */}
      {isParent && (
        <section data-testid="artwork-section" className="mt-6 tv-glass rounded-2xl p-6">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-display font-bold text-2xl text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-pink-400" /> Kids artwork
              </h2>
              <p className="text-white/60 text-sm">Every drawing your kid sent you — tap to view full-size.</p>
            </div>
            <span data-testid="artwork-count" className="text-xs bg-pink-500/20 text-pink-300 font-bold px-3 py-1 rounded-full">
              {artwork.length} piece{artwork.length === 1 ? "" : "s"}
            </span>
          </div>
          {artworkLoading && (
            <div data-testid="artwork-loading" className="mt-4 text-white/60 text-sm">Loading artwork…</div>
          )}
          {!artworkLoading && artwork.length === 0 && (
            <div data-testid="artwork-empty" className="mt-4 text-white/60 text-sm bg-black/30 border border-dashed border-white/10 rounded-xl p-6 text-center">
              <div className="text-4xl mb-2">🎨</div>
              No drawings yet. Your kid can send art from <span className="font-bold text-white/80">Kids TV → 🎨 Draw &amp; share</span>.
            </div>
          )}
          {artwork.length > 0 && (
            <div data-testid="artwork-grid" className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {artwork.map((d) => (
                <button
                  key={d.id}
                  data-testid={`artwork-tile-${d.id}`}
                  onClick={() => setArtworkOpen(d)}
                  className="group relative rounded-xl overflow-hidden bg-white ring-2 ring-white/10 hover:ring-pink-400 focus:ring-pink-400 focus:outline-none transition shadow"
                >
                  <div className="aspect-square w-full bg-white">
                    <img src={d.data_url} alt={d.title} className="w-full h-full object-contain" />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2 text-left">
                    <div className="text-white text-xs font-bold truncate">{d.title}</div>
                    <div className="text-white/70 text-[10px] truncate">by {d.kid_name || "your kid"}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Artwork lightbox */}
      {artworkOpen && (
        <div
          data-testid="artwork-lightbox"
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setArtworkOpen(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <div className="min-w-0">
                <div className="font-display font-black text-lg text-[#222] truncate">{artworkOpen.title}</div>
                <div className="text-xs text-[#666]">by {artworkOpen.kid_name || "your kid"} · {new Date(artworkOpen.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={artworkOpen.data_url}
                  download={`${(artworkOpen.title || "drawing").replace(/[^a-z0-9]/gi, "_")}.png`}
                  data-testid="artwork-download"
                  className="text-xs font-bold bg-pink-500 hover:bg-pink-400 text-white px-3 py-1.5 rounded-full"
                >Download</a>
                <button
                  data-testid={`artwork-delete-${artworkOpen.id}`}
                  onClick={async () => {
                    if (!window.confirm("Remove this drawing from your gallery?")) return;
                    try {
                      await api.delete(`/parent/kids/drawings/${artworkOpen.id}`);
                      setArtwork((a) => a.filter((x) => x.id !== artworkOpen.id));
                      setArtworkOpen(null);
                      toast.success("Drawing removed");
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}
                  className="text-xs font-bold bg-white text-red-600 border border-red-300 hover:bg-red-50 px-3 py-1.5 rounded-full inline-flex items-center gap-1"
                ><Trash2 className="w-3 h-3" /> Delete</button>
                <button
                  data-testid="artwork-close"
                  onClick={() => setArtworkOpen(null)}
                  className="w-8 h-8 rounded-full bg-black/5 hover:bg-black/10 text-[#222] flex items-center justify-center"
                ><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-[#f6f6f8] p-4 flex items-center justify-center">
              <img src={artworkOpen.data_url} alt={artworkOpen.title} className="max-w-full max-h-[70vh] object-contain rounded-lg shadow" />
            </div>
          </div>
        </div>
      )}

      {/* --- 6. Linked kids + upload review (only for linked-kid parents) --- */}
      {isParent && (
        <>
          {kids.length > 0 && (
            <section data-testid="linked-kids-section" className="mt-8">
              <h2 className="font-display font-bold text-2xl text-white mb-3">Your kids</h2>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {kids.map((k) => (
                  <div key={k.user_id} data-testid={`kid-card-${k.user_id}`} className="tv-glass rounded-xl p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-tv-brand/20 flex items-center justify-center"><Baby className="w-5 h-5 text-tv-brand" /></div>
                    <div className="min-w-0">
                      <div className="text-white font-semibold truncate">{k.name}</div>
                      <div className="text-xs text-white/50 truncate">{k.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="mt-8" data-testid="upload-review-section">
            <h2 className="font-display font-bold text-2xl text-white mb-3">Uploads to review</h2>
            {pending.length === 0 ? (
              <div className="text-white/60 text-sm" data-testid="parent-queue-empty">No uploads waiting for your review. You'll see items here when your kid submits new content.</div>
            ) : (
              <div className="space-y-3" data-testid="parent-review-queue">
                {pending.map((v) => (
                  <div key={v.id} className="tv-glass rounded-xl p-4 flex items-center gap-4">
                    {v.thumbnail_url && <img src={v.thumbnail_url} alt="" className="w-32 h-20 object-cover rounded" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-white text-lg truncate">{v.title}</div>
                      <div className="text-xs text-white/50 truncate">{v.creator_name} · {v.category}</div>
                      {v.description && <div className="text-sm text-white/70 mt-1 line-clamp-2">{v.description}</div>}
                    </div>
                    <button data-testid={`parent-approve-${v.id}`} onClick={() => approve(v.id)} className="bg-green-500 hover:bg-green-600 text-white font-bold px-4 py-2 rounded-full flex items-center gap-1"><Check className="w-4 h-4" /> Approve</button>
                    <button data-testid={`parent-reject-${v.id}`} onClick={() => reject(v.id)} className="bg-red-500/90 hover:bg-red-600 text-white font-bold px-4 py-2 rounded-full flex items-center gap-1"><X className="w-4 h-4" /> Reject</button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {/* Create / edit profile modals */}
      {newProfileOpen && (
        <ProfileEditor
          mode="create"
          avatars={profilesMeta.avatars}
          onClose={() => setNewProfileOpen(false)}
          onSaved={() => { setNewProfileOpen(false); loadAll(); }}
        />
      )}
      {editingProfile && (
        <ProfileEditor
          mode="edit"
          initial={editingProfile}
          avatars={profilesMeta.avatars}
          onClose={() => setEditingProfile(null)}
          onSaved={() => { setEditingProfile(null); loadAll(); }}
        />
      )}
    </div>
  );
}

function ProfileEditor({ mode, initial, avatars, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [avatar, setAvatar] = useState(initial?.avatar_emoji || (avatars[0] || "🦊"));
  const [limit, setLimit] = useState(initial?.daily_limit_min || "");
  const [ws, setWs] = useState(initial?.window_start_hh ?? "");
  const [we, setWe] = useState(initial?.window_end_hh ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    if (!avatar) { toast.error("Pick an avatar"); return; }
    setSaving(true);
    try {
      const body = { name: name.trim(), avatar_emoji: avatar };
      if (limit !== "" && !Number.isNaN(Number(limit))) body.daily_limit_min = Number(limit);
      if (ws !== "" && !Number.isNaN(Number(ws))) body.window_start_hh = Number(ws);
      if (we !== "" && !Number.isNaN(Number(we))) body.window_end_hh = Number(we);
      if (mode === "create") {
        await api.post("/kids/profiles", body);
        toast.success(`${body.name} added`);
      } else {
        await api.patch(`/kids/profiles/${initial.profile_id}`, body);
        toast.success("Profile updated");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="profile-editor" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#111] border border-white/10 rounded-2xl shadow-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-black text-2xl text-white">
            {mode === "create" ? "New kid profile" : `Edit ${initial?.name}`}
          </h3>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <div className="text-xs text-white/60 mb-1">Name</div>
            <input
              data-testid="profile-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={40}
              placeholder="e.g. Alex"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-accent"
            />
          </div>

          <div>
            <div className="text-xs text-white/60 mb-2">Pick an avatar</div>
            <div data-testid="profile-avatar-grid" className="flex flex-wrap gap-2">
              {avatars.map((a) => (
                <button
                  key={a}
                  data-testid={`profile-avatar-${a}`}
                  onClick={() => setAvatar(a)}
                  className={`w-12 h-12 rounded-xl text-2xl flex items-center justify-center transition ${
                    avatar === a ? "bg-tv-accent ring-2 ring-tv-accent scale-110" : "bg-white/5 hover:bg-white/10"
                  }`}
                >{a}</button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <div className="text-xs text-white/60 mb-1">Daily cap (min)</div>
              <input
                data-testid="profile-limit-input"
                type="number" min={10} max={720}
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                placeholder="parent default"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-accent"
              />
            </div>
            <div>
              <div className="text-xs text-white/60 mb-1">From hour</div>
              <input
                type="number" min={0} max={23}
                value={ws}
                onChange={(e) => setWs(e.target.value)}
                placeholder="optional"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-accent"
              />
            </div>
            <div>
              <div className="text-xs text-white/60 mb-1">Until hour</div>
              <input
                type="number" min={0} max={23}
                value={we}
                onChange={(e) => setWe(e.target.value)}
                placeholder="optional"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-accent"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button
            onClick={onClose}
            className="flex-1 bg-white/10 hover:bg-white/15 text-white font-bold py-2.5 rounded-full"
          >Cancel</button>
          <button
            data-testid="profile-save-btn"
            onClick={save}
            disabled={saving}
            className="flex-1 bg-tv-accent disabled:opacity-40 hover:bg-tv-accent/90 text-black font-bold py-2.5 rounded-full"
          >{saving ? "Saving…" : (mode === "create" ? "Add profile" : "Save changes")}</button>
        </div>
      </div>
    </div>
  );
}

function StatChip({ label, value, red }) {
  return (
    <div className={`px-3 py-2 rounded-lg border ${red ? "bg-red-500/15 border-red-500/40" : "bg-black/40 border-white/10"}`}>
      <div className="text-[10px] uppercase tracking-wider text-white/50">{label}</div>
      <div className={`text-lg font-black ${red ? "text-red-300" : "text-white"}`}>{value}</div>
    </div>
  );
}

/**
 * MyKidsWeek — "Your kid's week on Tap IN Kids" summary card. Renders the
 * last 7 days of screen-time per profile as a mini sparkline, top videos,
 * and learning-progress stats. Pulled from /api/kids/weekly-digest.
 */
function MyKidsWeek({ digest }) {
  if (!digest) return null;
  const hasData = (digest.profiles?.length || digest.default_bucket?.total_minutes > 0);
  return (
    <section data-testid="my-kids-week" className="mt-8 tv-glass rounded-2xl p-6 relative overflow-hidden">
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-gradient-to-br from-fuchsia-500/30 to-amber-400/20 blur-2xl pointer-events-none" />
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5 text-fuchsia-400" />
        <h2 className="font-display font-bold text-2xl text-white">My Kid's Week</h2>
        <span className="ml-2 text-xs bg-fuchsia-500/20 text-fuchsia-200 px-2 py-0.5 rounded-full font-bold">7-day summary</span>
      </div>
      <p className="text-white/60 text-sm mb-5">A weekly snapshot of what your kid watched, learned, and played on Tap IN Kids.</p>

      {!hasData && (
        <div className="text-white/55 text-sm">No activity yet this week. When your kid watches, learns, or plays, you'll see it here.</div>
      )}

      {hasData && (
        <>
          {/* Per-profile sparklines */}
          <div className="grid md:grid-cols-2 gap-3">
            {digest.profiles?.map((p) => (
              <WeekProfileCard key={p.profile_id} p={p} days={digest.days} />
            ))}
            {digest.default_bucket?.total_minutes > 0 && (
              <WeekProfileCard p={{ name: "No profile", avatar_emoji: "🏠", total_minutes: digest.default_bucket.total_minutes, per_day: digest.default_bucket.per_day }} days={digest.days} />
            )}
          </div>

          {/* Top shows + learning */}
          <div className="grid md:grid-cols-2 gap-3 mt-4">
            <div className="bg-black/40 border border-white/10 rounded-2xl p-4">
              <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-bold mb-2">Top shows this week</div>
              {(!digest.top_videos || digest.top_videos.length === 0) ? (
                <div className="text-white/55 text-sm">Not enough views to rank shows yet.</div>
              ) : (
                <ul className="space-y-2">
                  {digest.top_videos.map((v, i) => (
                    <li key={v.id} className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-fuchsia-500 text-white font-black text-sm flex items-center justify-center">{i + 1}</div>
                      <div className="w-16 h-10 rounded-md overflow-hidden bg-black/50 shrink-0">
                        {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" /> : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm font-bold truncate">{v.title}</div>
                        <div className="text-white/55 text-[11px] truncate">{v.creator_name} · {v.views} views</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-black/40 border border-white/10 rounded-2xl p-4">
              <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-bold mb-2">Learn &amp; Play</div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <StatChip label="Decks done" value={digest.learning?.decks_completed ?? 0} />
                <StatChip label="Games" value={digest.learning?.games_played ?? 0} />
                <StatChip label="Accuracy" value={digest.learning?.accuracy != null ? `${digest.learning.accuracy}%` : "—"} />
              </div>
              {digest.learning?.by_deck && Object.keys(digest.learning.by_deck).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(digest.learning.by_deck).map(([k, n]) => (
                    <span key={k} className="text-[11px] bg-white/10 text-white/80 font-bold px-2 py-0.5 rounded-full">{k} ×{n}</span>
                  ))}
                </div>
              )}
              {digest.bedtime_events > 0 && (
                <div className="mt-3 text-[11px] text-indigo-300 font-bold">🌙 {digest.bedtime_events} bedtime sessions this week</div>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

/** Per-profile weekly sparkline. Shows total minutes + a 7-bar bar chart. */
function WeekProfileCard({ p, days }) {
  const maxMin = Math.max(1, ...(p.per_day || []));
  return (
    <div data-testid={`kids-week-${p.profile_id || "default"}`} className="bg-black/40 border border-white/10 rounded-2xl p-4 flex gap-3">
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-fuchsia-500 to-sky-500 overflow-hidden shrink-0 flex items-center justify-center text-2xl">
        {p.avatar_image_url ? <img src={p.avatar_image_url} alt="" className="w-full h-full object-cover" /> : p.avatar_emoji}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-white truncate">{p.name}</div>
        <div className="text-white/55 text-[11px]">{p.handle ? `@${p.handle.replace(/^@/, "")} · ` : ""}<b className="text-white">{p.total_minutes}</b> min this week</div>
        <div className="flex items-end gap-1 mt-2 h-14">
          {(p.per_day || []).map((m, i) => {
            const h = Math.max(4, Math.round((m / maxMin) * 100));
            const label = days?.[i] ? days[i].slice(5) : "";
            return (
              <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${label}: ${m} min`}>
                <div className={`w-full rounded-t ${m === 0 ? "bg-white/10" : "bg-gradient-to-t from-fuchsia-500 to-amber-300"}`} style={{ height: `${h}%` }} />
                <div className="text-[9px] text-white/45">{["S","M","T","W","T","F","S"][new Date(days?.[i] || Date.now()).getDay()]}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


/**
 * VoiceSettingsSection — lets the parent pick the Learn & Play voice
 * preset (Kid / Woman / My recording) and record a custom audio intro
 * right from the browser using MediaRecorder. The clip (≤ 2MB) is
 * persisted as a data-URL in `kids_config.custom_voice_url` and used as
 * the Learn & Play "welcome" greeting when preset=custom.
 */
function VoiceSettingsSection() {
  const [preset, setPreset] = useState("kid");
  const [hasCustom, setHasCustom] = useState(false);
  const [customLabel, setCustomLabel] = useState("");
  const [customUrl, setCustomUrl] = useState(null);
  const [recording, setRecording] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recorder, setRecorder] = useState(null);
  const [label, setLabel] = useState("Mom's voice");
  const chunksRef = React.useRef([]);

  const load = async () => {
    try {
      const r = await api.get("/kids/config");
      setPreset(r.data?.voice_preset || "kid");
      setHasCustom(!!r.data?.has_custom_voice);
      setCustomLabel(r.data?.custom_voice_label || "");
      setCustomUrl(r.data?.custom_voice_url || null);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const choosePreset = async (p) => {
    if (p === "custom" && !hasCustom) { toast.error("Record your voice first"); return; }
    try {
      await api.post("/kids/voice-preset", { preset: p });
      setPreset(p);
      toast.success(`Voice set to ${p}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        if (blob.size > 2_000_000) { toast.error("Recording too long — keep it under ~20s"); return; }
        const reader = new FileReader();
        reader.onloadend = async () => {
          setSaving(true);
          try {
            await api.post("/kids/voice-pack", { data_url: reader.result, label });
            toast.success("Voice saved ✨");
            await load();
          } catch (err) { toast.error(formatApiError(err)); }
          finally { setSaving(false); }
        };
        reader.readAsDataURL(blob);
      };
      rec.start();
      setRecorder(rec);
      setRecording(true);
      setTimeout(() => { try { if (rec.state !== "inactive") rec.stop(); } catch {} setRecording(false); }, 20_000);
    } catch (e) { toast.error("Microphone permission denied"); }
  };
  const stopRec = () => { try { recorder?.stop(); } catch {} setRecording(false); };
  const deleteCustom = async () => {
    try { await api.delete("/kids/voice-pack"); toast.success("Voice cleared"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section data-testid="voice-settings" className="mt-8 tv-glass rounded-2xl p-6 relative overflow-hidden">
      <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-gradient-to-br from-cyan-500/30 to-fuchsia-500/20 blur-2xl pointer-events-none" />
      <div className="flex items-center gap-2 mb-1">
        <Volume2 className="w-5 h-5 text-cyan-300" />
        <h2 className="font-display font-bold text-2xl text-white">Learn &amp; Play voice</h2>
      </div>
      <p className="text-white/60 text-sm mb-5">Pick how the flashcards sound to your kid. You can even record your own voice — it plays as the welcome greeting.</p>

      <div className="grid md:grid-cols-3 gap-3">
        {[
          { k: "kid", title: "Soft kid voice", emoji: "🧒", desc: "Higher pitch, slow & gentle" },
          { k: "woman", title: "Soft woman voice", emoji: "👩", desc: "Calm, warm, adult tone" },
          { k: "custom", title: hasCustom ? (customLabel || "My recording") : "Record your voice", emoji: "🎙️", desc: hasCustom ? "Using your recording" : "Tap record below" },
        ].map((o) => (
          <button
            key={o.k}
            data-testid={`voice-preset-${o.k}`}
            onClick={() => choosePreset(o.k)}
            disabled={o.k === "custom" && !hasCustom}
            className={`relative text-left rounded-2xl p-4 border-2 transition ${
              preset === o.k
                ? "bg-cyan-500/15 border-cyan-400"
                : "bg-black/40 border-white/10 hover:border-white/30"
            } ${o.k === "custom" && !hasCustom ? "opacity-60" : ""}`}
          >
            <div className="text-4xl mb-1">{o.emoji}</div>
            <div className="font-display font-black text-white">{o.title}</div>
            <div className="text-white/60 text-xs mt-0.5">{o.desc}</div>
            {preset === o.k && <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-cyan-400 flex items-center justify-center"><Check className="w-3 h-3 text-black" /></div>}
          </button>
        ))}
      </div>

      <div className="mt-5 bg-black/40 border border-white/10 rounded-2xl p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <input
            data-testid="voice-label-input"
            value={label}
            onChange={(e) => setLabel(e.target.value.slice(0, 40))}
            placeholder="Label (e.g. Mom's voice)"
            className="flex-1 bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-white/30 outline-none focus:border-cyan-400"
          />
          {!recording ? (
            <button
              data-testid="voice-record-btn"
              onClick={startRec}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-black px-5 py-2.5 rounded-full disabled:opacity-50"
            ><Mic className="w-4 h-4" /> {saving ? "Saving…" : "Record (up to 20s)"}</button>
          ) : (
            <button
              data-testid="voice-stop-btn"
              onClick={stopRec}
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-red-600 font-black px-5 py-2.5 rounded-full animate-pulse"
            ><StopCircle className="w-4 h-4" /> Stop &amp; save</button>
          )}
          {hasCustom && (
            <>
              {customUrl && (
                <button
                  data-testid="voice-preview-btn"
                  onClick={() => { try { new Audio(customUrl).play(); } catch {} }}
                  className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white font-black px-5 py-2.5 rounded-full"
                ><PlayCircle className="w-4 h-4" /> Preview</button>
              )}
              <button
                data-testid="voice-delete-btn"
                onClick={deleteCustom}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-red-500/50 text-white font-black px-4 py-2.5 rounded-full"
              ><Trash className="w-4 h-4" /> Clear</button>
            </>
          )}
        </div>
        {recording && <div className="text-red-300 text-sm mt-2 font-bold flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500 animate-ping" /> Recording… speak naturally, like you're greeting your kid.</div>}
        <div className="text-white/55 text-xs mt-2">Tip: try "Hi sweetie, let's learn together on Tap IN Kids!" 🌈</div>
      </div>
    </section>
  );
}

